import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  Select,
  TextArea,
  PhoneInput,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import {
  FiImage,
  FiClock,
  FiDollarSign,
  FiAlignLeft,
  FiSettings,
  FiPhone,
} from "react-icons/fi";
import { FaBullhorn } from "react-icons/fa";

const Add = () => {
  const { createOneContent } = useDoSeeDataContext();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [title, setTitle] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [price, setPrice] = useState<any>(null);
  const [currency, setCurrency] = useState<any>(null);
  const [email, setEmail] = useState(null);
  const [website, setWebsite] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [adStatus, setAdStatus] = useState(1);
  const [description, setDescription] = useState<any>(null);

  const adRoutes = "/data/advertisement";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setTitle(null);
    setThumbnail(null);
    setThumbnailId(null);
    setStartDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
    setWebsite(null);
    setPrice(0);
    setEmail(null);
    setPhoneNumber(null);
    setCurrency(null);
    setDescription(null);
  };

  const responses = (error: any, message: any) => {
    setError(error);
    setLoading(false);
    setSuccess(!error);
    setErrorMessage(message);
  };

  const addOneAdHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title
        ? (title as any).toLowerCase().replace(/\s/g, "_")
        : null,
      description: description,
      start_date: startDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      price: price,
      website: website,
      currency: currency,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId,
      phone_number: phoneNumber,
      email_address: email,
      status: adStatus,
    };

    const { status, message } = await createOneContent({
      data: data,
      content: _TYPES_.ADV,
    });
    if (status === 200 || status === 201) {
      cleanFields();
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={adRoutes}
      eyebrow="DoSee Data • Ads"
      title="Add Ad"
      subtitle={`Create a sponsored ad for ${entry_label || "this destination"}.`}
      icon={<FaBullhorn className="h-5 w-5" />}
      loading={loading}
      submitLabel="Add Ad"
      onSubmit={addOneAdHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Ad Creative"
        hint="High-resolution image — JPG/PNG."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.ADV)}
        />
      </AddSection>

      <AddSection title="Ad Details" hint="Title that appears with the ad.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              required: true,
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Run Schedule"
        hint="When the ad is active."
        icon={<FiClock className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <InputField
            props={{
              id: "start_date",
              title: "Start Date",
              type: "date",
              required: true,
              value: startDate || "",
              onChange: (e: any) => {
                setStartDate(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "end_date",
              title: "End Date",
              type: "date",
              required: true,
              value: endDate || "",
              onChange: (e: any) => {
                setEndDate(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "start_time",
              title: "Start Time",
              type: "time",
              value: startTime || "",
              onChange: (e: any) => {
                setStartTime(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "end_time",
              title: "End Time",
              type: "time",
              value: endTime || "",
              onChange: (e: any) => {
                setEndTime(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Entrance / promo price."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InputField
            props={{
              id: "price",
              title: "Price (figures only)",
              type: "number",
              value: price || "",
              onChange: (e: any) => {
                setPrice(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "currency",
              title: "Currency",
              type: "text",
              value: currency || "",
              onChange: (e: any) => {
                setCurrency(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Contact Info"
        hint="How to reach the advertiser."
        icon={<FiPhone className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <InputField
            props={{
              id: "website",
              title: "Website",
              type: "text",
              required: true,
              value: website || "",
              onChange: (e: any) => {
                setWebsite(e.target.value);
                clearMessages();
              },
            }}
          />
          <PhoneInput
            setPhone={setPhoneNumber}
            phone={phoneNumber}
            required={false}
          />
          <InputField
            props={{
              id: "email",
              title: "Email Address",
              type: "email",
              value: email || "",
              onChange: (e: any) => {
                setEmail(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Visibility"
        hint="Toggle visibility of the ad."
        icon={<FiSettings className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Select
            props={{
              id: "ad_status",
              title: "Status",
              option: ["visible", "invisible"],
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setAdStatus(1);
                else setAdStatus(0);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="What the ad is offering."
        icon={<FiAlignLeft className="h-4 w-4" />}
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            required: true,
            value: description || "",
            onChange: (e: any) => {
              setDescription(e.target.value);
              clearMessages();
            },
          }}
        />
      </AddSection>
    </AddPageShell>
  );
};

export default Add;
